import clsx from 'clsx';
import React, { FC, useState, useEffect, Dispatch, SetStateAction, useRef, ChangeEvent, useMemo } from 'react'
import { ID, KTIcon } from '@/_metronic/helpers'
import { SalesRepartitionData } from './core/_models'
import { getSalesRepartition } from './core/_requests'

type Props = {
  userId?: ID
  from: Date
  to: Date
  timezone: string;
}

type IAgg = 'product' | 'brand';
interface AggProps {
  slug: IAgg;
  label: string;
}

const aggs: AggProps[] = [
  { slug: 'product', label: 'Product' },
  { slug: 'brand', label: 'Brand' }
]

interface LookupProps {
  setTerms: Dispatch<SetStateAction<string | undefined>>;
}

const Lookup: React.FC<LookupProps> = ({ setTerms }) => {
  const timerRef = useRef<NodeJS.Timeout | null>(null);

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;

    if (timerRef.current) {
      clearTimeout(timerRef.current);
    }

    timerRef.current = setTimeout(() => {
      setTerms(value || undefined);
    }, 300);
  };

  return (
    <div className='d-flex align-items-center position-relative my-1'>
      <KTIcon iconName='magnifier' className='fs-1 position-absolute ms-6' />
      <input
        type='text'
        data-kt-user-table-filter='search'
        className='form-control form-control-solid w-250px ps-14'
        placeholder="Search ..."
        onChange={handleChange}
      />
    </div>
  );
};


const ProductTable: FC<Props> = ({ userId, from, to, timezone }) => {
  const [rawData, setRawData] = useState<Array<SalesRepartitionData> | undefined>()
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [agg, setAgg] = useState<IAgg>('product');
  const [terms, setTerms] = useState<string | undefined>();

  useEffect(() => {
    const load = async () => {
      try {
        setIsLoading(true)
        const res = await getSalesRepartition({ agg, from, to, userId, timezone })
        setRawData(res || [])
      } catch (e: unknown) {
        console.log(e)
      } finally {
        setIsLoading(false)
      }
    }
    load()
  }, [userId, from, to, agg])

  // Filtrage côté client des données
  const data = useMemo(() => {
    if (!rawData) return undefined;
    if (!terms) return rawData;

    const lowerTerms = terms.toLowerCase();
    return rawData.filter(d =>
      d._id.toLowerCase().includes(lowerTerms)
    );
  }, [rawData, terms]);

  const total: SalesRepartitionData = (data || []).reduce(
    (agg, curr) => ({
      _id: "total",
      value: agg.value + curr.value,
      cogs: agg.cogs + curr.cogs,
      soldAmount: agg.soldAmount + curr.soldAmount
    }),
    {
      _id: "total",
      value: 0,
      cogs: 0,
      soldAmount: 0
    }
  );

  return (
    <div className="card">
      <div className='card-header border-0 pt-6'>
        <Lookup setTerms={setTerms} />
        <div className='card-toolbar'>
          <div className="pb-5 mt-5">
            {aggs.map((p) => (
              <button
                key={p.slug}
                className={clsx(
                  'btn btn-sm btn-color-muted btn-active btn-active-primary me-3',
                  { active: agg === p.slug }
                )}
                type="button"
                onClick={() => setAgg(p.slug)}
              >
                {p.label}
              </button>
            ))}
          </div>

        </div>
      </div>

      <div className="card-body py-4">
        <div className="table-responsive">
          <table className="table align-middle table-row-dashed fs-6 gy-5 dataTable no-footer">
            <thead>
              <tr className="text-start text-muted fw-bolder fs-7 text-uppercase gs-0">
                <th scope="col" className="text-start">Product</th>
                <th scope="col" className="text-center">Qty.</th>
                <th scope="col" className="text-center">Prc.</th>
                <th scope="col" className="text-end">COGS</th>
                <th scope="col" className="text-end">Amount</th>
                <th scope="col" className="text-end">Earnings</th>
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                <div>Loading ...</div>
              ) : (
                <>
                  {(data || []).map((d: SalesRepartitionData, index: number) => (
                    <tr key={`pt-${index}`} className="text-muted fw-bolder fs-7">
                      <td>{d._id}</td>
                      <td className="text-center">{d.value}</td>
                      <td className="text-center">{(d.value * 100 / total.value).toFixed(2)}%</td>
                      <td className="text-end">
                        {new Intl.NumberFormat('fr', {
                          style: 'currency',
                          currency: 'EUR',
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2
                        }).format(d.cogs)}
                      </td>
                      <td className="text-end">
                        {new Intl.NumberFormat('fr', {
                          style: 'currency',
                          currency: 'EUR',
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2
                        }).format(d.soldAmount)}
                      </td>
                      <td className="text-end">
                        {new Intl.NumberFormat('fr', {
                          style: 'currency',
                          currency: 'EUR',
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2
                        }).format(d.soldAmount - d.cogs)}
                      </td>
                    </tr>
                  ))}
                </>
              )}
            </tbody>
            {!isLoading && data && data.length > 0 && (
              <tfoot>
                <tr className="text-start text-muted fw-bolder fs-7 text-uppercase gs-0">
                  <td>Total</td>
                  <td className='text-center'>{total.value}</td>
                  <td className='text-center'>100%</td>
                  <td className='text-end'>
                    {new Intl.NumberFormat('fr', {
                      style: 'currency',
                      currency: 'EUR',
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2
                    }).format(total.cogs)}
                  </td>

                  <td className='text-end'>
                    {new Intl.NumberFormat('fr', {
                      style: 'currency',
                      currency: 'EUR',
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2
                    }).format(total.soldAmount)}
                  </td>

                  <td className='text-end'>
                    {new Intl.NumberFormat('fr', {
                      style: 'currency',
                      currency: 'EUR',
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2
                    }).format(total.soldAmount - total.cogs)}
                  </td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      </div>
    </div>
  );
}

export default ProductTable;
